import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import hackathonApi, { attendeeErrorMessage } from '../config/hackathonApi';
import { identificationErrorMessage } from '../config/attendeeErrors';
import PresenceMap from '../components/PresenceMap';
import QrScannerDialog from '../components/QrScannerDialog';
import {
  FacmarBadge,
  HackathonShell,
  SectionTitle,
  StepProgress,
  isFacmarContext,
} from '../components/hackathon/HackathonBrand';
import { hackathonColors, hackathonMapColors } from '../components/hackathon/hackathonTheme';
import { parseScannedQr } from '../utils/qrScanner';
import {
  ATTENDEE_SESSION_ENDED,
  clearAttendeeSession,
  readAttendeeSession,
  saveAttendeeSession,
} from '../utils/attendeeSession';
import {
  assessReading,
  cancellationState,
  confirmationReadingProblem,
  enrollmentLabel,
  eventCenter,
  fixedLinkNotice,
  formatAge,
  formatLocalTimestamp,
  geolocationFailure,
  parseEventId,
  parsePresenceCode,
  parsePresenceTarget,
  presenceAvailability,
  presenceTargetValue,
  presenceWindowText,
  readingStatusLabel,
  serverClockOffset,
} from '../utils/hackathonFlow';

const SESSION_ENDED_MESSAGE = 'Sua sessão terminou. Valide o e-mail novamente para continuar.';

const readingSeverity = { inside: 'success', outside: 'error', imprecise: 'warning', unknown: 'warning' };

function CancellationLock({ id, state }) {
  return (
    <Stack id={id} direction="row" spacing={1} alignItems="flex-start" sx={{ color: 'text.secondary' }}>
      <LockOutlinedIcon fontSize="small" aria-hidden="true" sx={{ mt: 0.25, color: 'warning.main' }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" component="p" sx={{ color: 'text.primary' }}>{state.title}</Typography>
        <Typography variant="body2" component="p">{state.description}</Typography>
      </Box>
    </Stack>
  );
}

const actionSx = { minHeight: 48 };
const panelSx = { p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'rgba(18, 32, 61, 0.45)' };

const clockFormat = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const availabilityTone = { open: 'success', upcoming: 'info', closed: 'default', unknown: 'warning' };

function WindowCaption({ windows }) {
  return (
    <Typography variant="caption" color="text.secondary" component="p">
      {presenceWindowText(windows)}
    </Typography>
  );
}

function AvailabilityChips({ windows, nowMs }) {
  const { checkIn, checkOut } = presenceAvailability(windows, nowMs);
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {[[checkIn, LoginIcon], [checkOut, LogoutIcon]].map(([item, Icon]) => (
        <Chip
          key={item.label}
          size="small"
          icon={<Icon />}
          label={item.label}
          color={availabilityTone[item.state]}
          variant={item.state === 'open' ? 'filled' : 'outlined'}
          sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
        />
      ))}
    </Stack>
  );
}

function SummaryRow({ label, children }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' }, gap: { xs: 0.25, sm: 1.5 }, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography component="dt" variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{label}</Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</Box>
    </Box>
  );
}

const readPosition = () => new Promise((resolve, reject) => {
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    reject(new Error(geolocationFailure(null, false)));
    return;
  }
  if (!navigator.geolocation) {
    reject(new Error(geolocationFailure({ code: 2 }, true)));
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (position) => resolve({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      captured_at: new Date(position.timestamp).toISOString(),
    }),
    (error) => reject(new Error(geolocationFailure(error, true))),
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
  );
});

export default function HackathonPage() {
  const [step, setStep] = useState(() => (readAttendeeSession() ? 'restoring' : 'identify'));
  const [form, setForm] = useState({
    kind: 'student',
    nome: '',
    email: '',
    matricula: '',
    code: '',
    eventId: sessionStorage.getItem('hackathonEventId') || '',
    pin: '',
    fixedRef: '',
  });
  const [eventInfo, setEventInfo] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [activities, setActivities] = useState([]);
  const [targetId, setTargetId] = useState('');
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [openEvents, setOpenEvents] = useState([]);
  const [eventsError, setEventsError] = useState('');
  const [presenceStep, setPresenceStep] = useState(0);
  const [locationReading, setLocationReading] = useState(null);
  const [gpsRules, setGpsRules] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [clockOffset, setClockOffset] = useState(0);
  const confirmRef = useRef(null);

  const resetJourney = useCallback(() => {
    setPresenceStep(0);
    setHistory([]);
    setEnrollment(null);
    setEventInfo(null);
    setActivities([]);
    setLocationReading(null);
    setForm((current) => ({ ...current, code: '', pin: '', fixedRef: '' }));
  }, []);

  useEffect(() => {
    if (step !== 'restoring') return undefined;
    let active = true;
    hackathonApi.get('/me')
      .then((response) => {
        if (!active) return;
        const current = readAttendeeSession();
        if (current) saveAttendeeSession(current.token, response?.data?.expiresAt || current.expiresAt);
        setStep('journey');
      })
      .catch((reason) => {
        if (!active) return;
        if (reason?.response?.status === 401) {
          clearAttendeeSession();
          setError(SESSION_ENDED_MESSAGE);
          setStep('identify');
          return;
        }
        setStep('journey');
      });
    return () => {
      active = false;
    };
  }, [step]);

  useEffect(() => {
    const onEnded = () => {
      resetJourney();
      setMessage('');
      setError(SESSION_ENDED_MESSAGE);
      setStep('identify');
    };
    window.addEventListener(ATTENDEE_SESSION_ENDED, onEnded);
    return () => window.removeEventListener(ATTENDEE_SESSION_ENDED, onEnded);
  }, [resetJourney]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const resendSeconds = Math.max(0, Math.ceil((resendAt - now) / 1000));

  const loadHistory = useCallback(async () => {
    const response = await hackathonApi.get('/presence');
    setHistory(response.data.presence || []);
  }, []);

  // silent: atualiza a elegibilidade sem limpar mensagens nem a seleção; erros sobem para quem chamou.
  const loadEvent = useCallback(async (rawId, preferredTarget = null, { silent = false } = {}) => {
    const eventId = parseEventId(rawId);
    if (!eventId) {
      if (silent) return null;
      setError('Informe um evento válido antes de consultar as atividades.');
      setEventInfo(null);
      setActivities([]);
      return null;
    }
    if (!silent) {
      setBusy('event');
      setError('');
      setMessage('');
    }
    try {
      const [activityResponse, enrollmentResponse] = await Promise.all([
        hackathonApi.get(`/events/${eventId}/activities`),
        hackathonApi.get('/me/enrollments'),
      ]);
      const current = (enrollmentResponse.data.enrollments || []).find(
        (item) => Number(item.event_id) === eventId
      );
      const offset = serverClockOffset(activityResponse.data.server_time, Date.now());
      const fresh = {
        event: activityResponse.data.event,
        activities: activityResponse.data.activities || [],
        enrollment: current && current.status === 'active' ? current : null,
        offset,
      };
      setClockOffset(offset);
      setEventInfo(fresh.event);
      setGpsRules(activityResponse.data.gps_rules || null);
      setActivities(fresh.activities);
      setEnrollment(fresh.enrollment);
      if (silent) {
        setTargetId((selected) => preferredTarget || selected || presenceTargetValue('event', eventId));
        return fresh;
      }
      setTargetId(preferredTarget || presenceTargetValue('event', eventId));
      sessionStorage.setItem('hackathonEventId', String(eventId));
      if (fresh.activities.length === 0) {
        setMessage('Este evento não tem atividades cadastradas.');
      }
      return fresh;
    } catch (reason) {
      if (silent) throw reason;
      setEventInfo(null);
      setActivities([]);
      setEnrollment(null);
      setError(reason?.response?.status === 404
        ? 'Evento não encontrado.'
        : attendeeErrorMessage(reason));
      return null;
    } finally {
      if (!silent) setBusy('');
    }
  }, []);

  const tracking = step === 'otp'
    || (step === 'journey' && (presenceStep === 2
      || (presenceStep === 1 && Boolean(locationReading))
      || (presenceStep === 0 && Boolean(eventInfo))));
  useEffect(() => {
    if (!tracking) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [tracking]);

  const readLocation = async () => {
    setBusy('gps');
    setError('');
    try {
      const position = await readPosition();
      setLocationReading({ ...position, readAt: Date.now() });
      setNow(Date.now());
      setMessage('Localização lida. Ela ainda não registra presença.');
    } catch (reason) {
      setLocationReading(null);
      setError(reason.message);
    } finally {
      setBusy('');
    }
  };

  const center = useMemo(() => eventCenter(eventInfo), [eventInfo]);
  const assessment = assessReading({
    center,
    reading: locationReading,
    maxAccuracy: gpsRules?.max_accuracy_m,
    maxAgeSeconds: gpsRules?.max_reading_age_s,
    nowMs: now,
  });
  const selectedTarget = parsePresenceTarget(targetId);
  const selectedWindows = selectedTarget?.targetType === 'activity'
    ? activities.find((item) => item.id === selectedTarget.targetId)?.presence_windows
    : eventInfo?.presence_windows;

  useEffect(() => {
    if (step !== 'journey') return undefined;
    loadHistory().catch((reason) => setError(attendeeErrorMessage(reason)));
    hackathonApi.get('/events/open')
      .then((response) => {
        setOpenEvents(response.data.events || []);
        setEventsError('');
      })
      .catch(() => setEventsError('Não foi possível carregar os eventos.'));
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) {
      hackathonApi.get(`/checkin-links/${ref}`)
        .then(async (response) => {
          const link = response.data.link;
          setForm((current) => ({ ...current, eventId: String(link.eventId), pin: '', fixedRef: ref }));
          await loadEvent(link.eventId, link.subEventId ? presenceTargetValue('activity', link.subEventId) : null);
          setPresenceStep(1);
          setMessage(fixedLinkNotice(link));
        })
        .catch((reason) => setError(attendeeErrorMessage(reason)));
    } else {
      const saved = sessionStorage.getItem('hackathonEventId');
      if (parseEventId(saved)) loadEvent(saved);
    }
    return undefined;
  }, [step, loadEvent, loadHistory]);

  const requestCode = async () => {
    setBusy('otp');
    setError('');
    try {
      const response = await hackathonApi.post('/identify', {
        kind: form.kind,
        nome: form.nome,
        email: form.email,
        matricula: form.kind === 'student' ? form.matricula : undefined,
      });
      setMessage(response.data.message || 'Se os dados estiverem corretos, enviamos um código para o e-mail informado.');
      setResendAt(Date.now() + 30000);
      setNow(Date.now());
      setStep('otp');
    } catch (reason) {
      setError(identificationErrorMessage(reason, 'identify'));
      if (reason?.response?.data?.code === 'rate_limited') {
        setResendAt(Date.now() + 30000);
        setNow(Date.now());
      }
    } finally {
      setBusy('');
    }
  };

  const verify = async () => {
    setBusy('otp');
    setError('');
    try {
      const response = await hackathonApi.post('/otp/verify', { email: form.email, code: form.code });
      saveAttendeeSession(response.data.token, response.data.expiresAt);
      setMessage('E-mail validado. Informe o número do evento para continuar.');
      setStep('journey');
    } catch (reason) {
      setError(identificationErrorMessage(reason, 'verify'));
    } finally {
      setBusy('');
    }
  };

  const enroll = async () => {
    const eventId = parseEventId(form.eventId);
    if (!eventId) {
      setError('Informe um evento válido antes de consultar as atividades.');
      return;
    }
    setBusy('enroll');
    setError('');
    try {
      await hackathonApi.post('/enrollments', { eventId });
      setMessage('Inscrição no evento confirmada.');
      await loadEvent(eventId);
    } catch (reason) {
      setError(attendeeErrorMessage(reason));
    } finally {
      setBusy('');
    }
  };

  const refuseCancellation = async (reason, eventId) => {
    setError(attendeeErrorMessage(reason));
    if (reason?.response?.status === 409) await loadEvent(eventId, null, { silent: true }).catch(() => null);
  };

  const cancelEnrollment = async () => {
    const eventId = parseEventId(form.eventId);
    if (!eventId) return;
    setBusy('enroll');
    setError('');
    setMessage('');
    try {
      const fresh = await loadEvent(eventId, null, { silent: true });
      if (!fresh.enrollment) {
        setMessage('Sua inscrição no evento já não estava ativa.');
        return;
      }
      const state = cancellationState(fresh.event, {
        nowMs: Date.now() + fresh.offset,
        scope: 'event',
        linked: fresh.activities.filter((item) => item.enrollment_status === 'active'),
      });
      if (state.state !== 'allowed') {
        setError(state.description);
        return;
      }
      await hackathonApi.delete(`/enrollments/${eventId}`);
      setMessage('Inscrição cancelada.');
      await loadEvent(eventId, null, { silent: true });
    } catch (reason) {
      await refuseCancellation(reason, eventId);
    } finally {
      setBusy('');
    }
  };

  const enrollActivity = async (activityId) => {
    setBusy(`activity-${activityId}`);
    setError('');
    try {
      await hackathonApi.post(`/activities/${activityId}/enroll`);
      setMessage('Inscrição na atividade confirmada.');
      await loadEvent(form.eventId);
    } catch (reason) {
      setError(attendeeErrorMessage(reason));
    } finally {
      setBusy('');
    }
  };

  const cancelActivity = async (activityId) => {
    setBusy(`activity-${activityId}`);
    setError('');
    setMessage('');
    try {
      const fresh = await loadEvent(form.eventId, null, { silent: true });
      const activity = fresh?.activities.find((item) => item.id === activityId);
      if (!activity || activity.enrollment_status !== 'active') {
        setMessage('Sua inscrição nesta atividade já não estava ativa.');
        return;
      }
      const state = cancellationState(activity, { nowMs: Date.now() + fresh.offset });
      if (state.state !== 'allowed') {
        setError(state.description);
        return;
      }
      await hackathonApi.delete(`/activities/${activityId}/enroll`);
      setMessage('Inscrição na atividade cancelada.');
      await loadEvent(form.eventId, null, { silent: true });
    } catch (reason) {
      await refuseCancellation(reason, form.eventId);
    } finally {
      setBusy('');
    }
  };

  const signOut = async () => {
    setBusy('logout');
    await hackathonApi.post('/logout').catch(() => null);
    clearAttendeeSession();
    sessionStorage.removeItem('hackathonEventId');
    resetJourney();
    setBusy('');
    setError('');
    setMessage('Você saiu. Para registrar presença de novo, valide o e-mail.');
    setStep('identify');
  };

  const goToStep = (next) => {
    setError('');
    setMessage('');
    setPresenceStep(next);
  };

  const openScanner = () => {
    setError('');
    setMessage('');
    setScannerOpen(true);
  };

  const closeScanner = useCallback(() => setScannerOpen(false), []);

  useEffect(() => {
    if (step !== 'journey' || presenceStep !== 2) setScannerOpen(false);
  }, [step, presenceStep]);

  const handleScanned = useCallback(async (value) => {
    setScannerOpen(false);
    const scanned = parseScannedQr(value);
    if (scanned.kind === 'fixed') {
      try {
        const response = await hackathonApi.get(`/checkin-links/${scanned.ref}`);
        const link = response.data.link;
        setForm((current) => ({ ...current, eventId: String(link.eventId), pin: '', fixedRef: scanned.ref }));
        await loadEvent(link.eventId, link.subEventId ? presenceTargetValue('activity', link.subEventId) : null);
        setPresenceStep(1);
        setMessage(fixedLinkNotice(link));
      } catch (reason) {
        setError(attendeeErrorMessage(reason));
      }
      return;
    }
    if (scanned.kind === 'challenge') {
      setForm((current) => ({ ...current, pin: scanned.value, fixedRef: '' }));
      setMessage('QR lido. A presença ainda não foi registrada: confira os dados e confirme a entrada ou a saída.');
      window.setTimeout(() => confirmRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 150);
      return;
    }
    setError('O QR lido não é um código de presença do PontoNow. Tente outro QR ou digite o PIN.');
  }, [loadEvent]);

  const presence = async (operation) => {
    const usingFixed = Boolean(form.fixedRef);
    const parsed = usingFixed ? { ok: true } : parsePresenceCode(form.pin);
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }
    const target = parsePresenceTarget(targetId);
    if (!target) {
      setError('Escolha o evento ou a atividade antes de confirmar a presença.');
      return;
    }
    setBusy(operation);
    setError('');
    setMessage('');
    try {
      const position = await readPosition();
      const readAt = Date.now();
      setLocationReading({ ...position, readAt });
      setNow(readAt);
      const problem = confirmationReadingProblem({
        reading: position,
        maxAccuracy: gpsRules?.max_accuracy_m,
        maxAgeSeconds: gpsRules?.max_reading_age_s,
        nowMs: readAt,
      });
      if (problem) {
        setError(problem);
        return;
      }
      const response = await hackathonApi.post(`/presence/${operation}`, {
        ...target,
        ...position,
        ...(usingFixed
          ? { checkinRef: form.fixedRef }
          : { pin: parsed.pin, challenge: parsed.challenge }),
      });
      setMessage(response.data.message || (operation === 'check-in' ? 'Entrada confirmada.' : 'Saída confirmada.'));
      setPresenceStep(3);
      await Promise.all([
        loadHistory(),
        loadEvent(form.eventId, null, { silent: true }).catch(() => null),
      ]);
    } catch (reason) {
      setError(reason?.response ? attendeeErrorMessage(reason) : reason.message);
    } finally {
      setBusy('');
    }
  };

  const inJourney = step === 'journey';
  const clock = (tracking ? now : Date.now()) + clockOffset;
  const eventCancellation = enrollment && eventInfo
    ? cancellationState(eventInfo, {
      nowMs: clock,
      scope: 'event',
      linked: activities.filter((item) => item.enrollment_status === 'active'),
    })
    : null;
  const selectedName = selectedTarget?.targetType === 'activity'
    ? activities.find((item) => item.id === selectedTarget.targetId)?.nome
    : eventInfo?.nome;
  const availability = selectedTarget ? presenceAvailability(selectedWindows, clock) : null;
  const exitFirst = availability?.checkOut.state === 'open' && availability?.checkIn.state !== 'open';
  const typedCode = form.pin.trim();
  let codeLabel = 'Nenhum código informado';
  if (form.fixedRef) codeLabel = 'QR fixo da atividade';
  else if (typedCode.startsWith('pn1.')) codeLabel = 'QR temporário lido';
  else if (/^\d{6}$/.test(typedCode)) codeLabel = 'PIN informado';
  const codeReady = codeLabel !== 'Nenhum código informado';
  const showFacmar = isFacmarContext(eventInfo?.nome, selectedName);
  const listedEvents = openEvents.filter((item) => (
    !item.status || item.status === 'upcoming' || item.status === 'ongoing'
  ));
  const eventOptions = eventInfo && !listedEvents.some((item) => Number(item.id) === Number(eventInfo.id))
    ? [...listedEvents, eventInfo]
    : listedEvents;
  const eventOptionLabel = (item) => {
    const when = formatLocalTimestamp(item.start_datetime);
    if (item.status === 'upcoming') return `${item.nome} · ${when} · Em breve`;
    if (item.status === 'ongoing') return `${item.nome} · ${when} · Em andamento`;
    return `${item.nome} · ${when}`;
  };

  const feedback = (
    <>
      {message ? <Alert severity="success">{message}</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
    </>
  );
  const inlineFeedback = inJourney && presenceStep >= 2;

  return (
    <HackathonShell>
      <Stack spacing={2}>
        {inJourney ? <StepProgress current={presenceStep === 3 ? 4 : presenceStep} /> : null}
        {inJourney ? (
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="text" size="small" disabled={busy === 'logout'} onClick={signOut}>
              {busy === 'logout' ? 'Saindo...' : 'Sair'}
            </Button>
          </Stack>
        ) : null}
        {!inlineFeedback ? feedback : null}

        {step === 'restoring' ? (
          <Card><CardContent>
            <Stack spacing={2} alignItems="center" role="status" aria-live="polite">
              <CircularProgress size={32} />
              <Typography>Restaurando sua sessão...</Typography>
            </Stack>
          </CardContent></Card>
        ) : null}

        {step === 'identify' ? (
          <Card><CardContent>
            <Stack spacing={2}>
              <SectionTitle icon={BadgeOutlinedIcon} eyebrow="Acesso">Identificação</SectionTitle>
              <Typography variant="body2" color="text.secondary">
                Informe seus dados para receber um código de acesso por e-mail.
              </Typography>
              <TextField select label="Participação" value={form.kind} onChange={update('kind')}>
                <MenuItem value="student">Aluno</MenuItem>
                <MenuItem value="external">Externo</MenuItem>
              </TextField>
              <TextField label="Nome" value={form.nome} onChange={update('nome')} autoComplete="name" />
              <TextField label="E-mail" type="email" value={form.email} onChange={update('email')} autoComplete="email" />
              {form.kind === 'student' ? <TextField label="Matrícula" value={form.matricula} onChange={update('matricula')} /> : null}
              <Button variant="contained" size="large" sx={actionSx} disabled={busy === 'otp'} onClick={requestCode}>
                {busy === 'otp' ? 'Enviando código...' : 'Receber código'}
              </Button>
            </Stack>
          </CardContent></Card>
        ) : null}

        {step === 'otp' ? (
          <Card><CardContent>
            <Stack spacing={2}>
              <SectionTitle icon={MarkEmailReadOutlinedIcon} eyebrow="Acesso">Código do e-mail</SectionTitle>
              <Typography variant="body2" color="text.secondary">Digite o código recebido. Ele expira em alguns minutos e só pode ser usado uma vez.</Typography>
              <TextField label="Código recebido" value={form.code} onChange={update('code')} disabled={Boolean(busy)} inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', 'aria-label': 'Código recebido' }} />
              <Button variant="contained" size="large" sx={actionSx} disabled={busy === 'otp' || !form.code.trim()} onClick={verify}>
                {busy === 'otp' ? 'Validando código...' : 'Validar código'}
              </Button>
              <Button variant="outlined" size="large" sx={actionSx} disabled={busy === 'otp' || resendSeconds > 0} onClick={requestCode}>
                {resendSeconds > 0 ? `Reenviar código em ${resendSeconds}s` : 'Reenviar código'}
              </Button>
            </Stack>
          </CardContent></Card>
        ) : null}

        {inJourney && presenceStep === 0 ? (
          <>
            <Card><CardContent>
              <Stack spacing={2}>
                <SectionTitle icon={EventAvailableIcon} eyebrow="Etapa 1">Evento e atividade</SectionTitle>
                <TextField
                  select
                  label="Evento"
                  value={eventOptions.some((item) => String(item.id) === form.eventId) ? form.eventId : ''}
                  onChange={(event) => {
                    const next = event.target.value;
                    setForm((current) => ({ ...current, eventId: next }));
                    if (parseEventId(next)) loadEvent(next);
                  }}
                >
                  <MenuItem value="">Selecione</MenuItem>
                  {eventOptions.map((item) => (
                    <MenuItem key={item.id} value={String(item.id)} sx={{ whiteSpace: 'normal' }}>
                      {eventOptionLabel(item)}
                    </MenuItem>
                  ))}
                </TextField>
                {eventsError ? <Alert severity="error">{eventsError}</Alert> : null}
                {!eventsError && listedEvents.length === 0 ? (
                  <Typography color="text.secondary">Nenhum evento publicado no momento.</Typography>
                ) : null}
                {eventInfo ? (
                  <Box sx={panelSx}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" component="h3">{eventInfo.nome}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatLocalTimestamp(eventInfo.start_datetime)} – {formatLocalTimestamp(eventInfo.end_datetime)}
                          </Typography>
                        </Box>
                        {showFacmar ? <FacmarBadge /> : null}
                      </Stack>
                      <AvailabilityChips windows={eventInfo.presence_windows} nowMs={clock} />
                      <WindowCaption windows={eventInfo.presence_windows} />
                      <Typography variant="body2">
                        Inscrição: <Box component="strong" sx={{ color: enrollment ? 'success.main' : 'text.primary' }}>{enrollmentLabel(enrollment?.status)}</Box>
                      </Typography>
                    </Stack>
                  </Box>
                ) : null}
                <Button variant="outlined" size="large" sx={actionSx} disabled={busy === 'enroll' || !eventInfo || Boolean(enrollment)} onClick={enroll}>
                  {enrollment ? 'Inscrito no evento' : busy === 'enroll' ? 'Inscrevendo...' : 'Inscrever no evento'}
                </Button>
                {eventCancellation?.state === 'allowed' ? (
                  <Button variant="text" disabled={busy === 'enroll'} onClick={cancelEnrollment}>Cancelar inscrição no evento</Button>
                ) : null}
                {eventCancellation && eventCancellation.state !== 'allowed' ? (
                  <CancellationLock id="event-cancellation-lock" state={eventCancellation} />
                ) : null}
              </Stack>
            </CardContent></Card>

            <Card><CardContent>
              <Stack spacing={2}>
                <SectionTitle eyebrow="Programação">Atividades</SectionTitle>
                {!eventInfo ? <Typography color="text.secondary">Selecione um evento para ver as atividades.</Typography> : null}
                {eventInfo && activities.length === 0 ? <Typography color="text.secondary">Nenhuma atividade neste evento.</Typography> : null}
                {activities.map((activity) => {
                  const full = activity.remaining === 0;
                  const enrolled = activity.enrollment_status === 'active';
                  const cancellation = enrolled ? cancellationState(activity, { nowMs: clock }) : null;
                  const lockId = `activity-${activity.id}-cancellation-lock`;
                  return (
                    <Box key={activity.id} sx={panelSx}>
                      <Stack spacing={1}>
                        <Typography variant="h6" component="h3">{activity.nome}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatLocalTimestamp(activity.start_datetime)} – {formatLocalTimestamp(activity.end_datetime)}
                        </Typography>
                        <AvailabilityChips windows={activity.presence_windows} nowMs={clock} />
                        <WindowCaption windows={activity.presence_windows} />
                        <Typography variant="body2">
                          {activity.remaining == null ? 'Sem limite de vagas' : full ? 'Sem vagas' : `Vagas restantes: ${activity.remaining}`}
                          {' · '}
                          <Box component="strong" sx={{ color: enrolled ? 'success.main' : 'text.secondary' }}>{enrolled ? 'Inscrito' : 'Não inscrito'}</Box>
                        </Typography>
                        {enrolled && cancellation.state === 'allowed' ? (
                          <Button sx={actionSx} variant="outlined" disabled={busy === `activity-${activity.id}`} onClick={() => cancelActivity(activity.id)}>
                            {busy === `activity-${activity.id}` ? 'Cancelando...' : 'Cancelar inscrição'}
                          </Button>
                        ) : enrolled ? (
                          <CancellationLock id={lockId} state={cancellation} />
                        ) : (
                          <Button sx={actionSx} variant="outlined" disabled={Boolean(busy) || full || !enrollment} onClick={() => enrollActivity(activity.id)}>
                            {full ? 'Sem vagas' : busy === `activity-${activity.id}` ? 'Inscrevendo...' : 'Inscrever-se'}
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </CardContent></Card>

            <Button variant="contained" size="large" sx={actionSx} disabled={!eventInfo} onClick={() => goToStep(1)}>
              Continuar para a localização
            </Button>
          </>
        ) : null}

        {inJourney && presenceStep === 1 ? (
          <Card><CardContent>
            <Stack spacing={2}>
              <SectionTitle icon={PlaceOutlinedIcon} eyebrow="Etapa 2">Localização</SectionTitle>
              <Typography variant="body2" color="text.secondary">
                A leitura é enviada só na confirmação. O servidor decide se ela vale.
              </Typography>
              {eventInfo && !center ? (
                <Alert severity="warning">
                  O local deste evento não foi configurado. Avise a organização: sem o local, o servidor recusa a presença.
                </Alert>
              ) : null}
              <PresenceMap center={center} reading={locationReading} colors={hackathonMapColors} />
              {assessment ? (
                <Box sx={panelSx}>
                  <Alert severity={readingSeverity[assessment.status]} sx={{ mb: 1 }}>
                    {readingStatusLabel(assessment.status)}
                  </Alert>
                  <Typography variant="body2">
                    Distância até o centro: <strong>{assessment.distance} m</strong>
                    {center?.radius ? ` · raio autorizado: ${center.radius} m` : ''}
                  </Typography>
                  <Typography variant="body2">
                    Precisão do GPS: <strong>{assessment.accuracy} m</strong>
                    {gpsRules?.max_accuracy_m ? ` · aceita até ${gpsRules.max_accuracy_m} m` : ''}
                  </Typography>
                  <Typography variant="body2">Leitura: {formatAge(assessment.ageSeconds)}</Typography>
                  {assessment.stale ? (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                      Esta leitura já é antiga. Ao confirmar, o GPS é lido novamente.
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
              {!assessment && locationReading ? (
                <Typography variant="body2">
                  Precisão do GPS: {Math.round(locationReading.accuracy)} m · {formatAge(Math.round((now - locationReading.readAt) / 1000))}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                Distância e situação são estimativas do aparelho. O servidor confere tudo na confirmação.
              </Typography>
              <Button variant={locationReading ? 'outlined' : 'contained'} size="large" sx={actionSx} startIcon={<MyLocationIcon />} disabled={busy === 'gps'} onClick={readLocation}>
                {busy === 'gps' ? 'Lendo GPS...' : locationReading ? 'Atualizar localização' : 'Ler minha localização'}
              </Button>
              <Button variant="contained" size="large" sx={actionSx} disabled={!locationReading} onClick={() => goToStep(2)}>Continuar para o código</Button>
              <Button variant="text" onClick={() => goToStep(0)}>Voltar</Button>
            </Stack>
          </CardContent></Card>
        ) : null}

        {inJourney && presenceStep === 2 ? (
          <>
            <Card><CardContent>
              <Stack spacing={2}>
                <SectionTitle icon={QrCode2Icon} eyebrow="Etapa 3">Código da atividade</SectionTitle>
                <Typography variant="body2" color="text.secondary">
                  Leia o QR Code exibido pela organização ou digite o PIN. O código só é conferido na confirmação.
                </Typography>
                <Button variant="contained" size="large" sx={actionSx} startIcon={<QrCodeScannerIcon />} onClick={openScanner}>
                  Ler QR Code
                </Button>
                <Divider sx={{ color: 'text.secondary', fontSize: 12 }}>ou digite o PIN</Divider>
                <TextField
                  label="PIN de 6 dígitos ou código QR"
                  value={form.pin}
                  onChange={(event) => setForm((current) => ({ ...current, pin: event.target.value, fixedRef: '' }))}
                  inputProps={{ autoComplete: 'off', spellCheck: false }}
                />
                {codeReady ? (
                  <Chip icon={<CheckCircleIcon />} color="success" variant="outlined" label={`${codeLabel}: pronto para confirmar`} sx={{ alignSelf: 'flex-start', maxWidth: '100%' }} />
                ) : null}
                {form.fixedRef ? (
                  <Typography variant="body2" color="text.secondary">O QR fixo substitui o PIN nesta confirmação.</Typography>
                ) : null}
              </Stack>
            </CardContent></Card>

            <Card ref={confirmRef} sx={{ scrollMarginTop: 16, borderColor: 'rgba(224, 87, 122, 0.35)' }}><CardContent>
              <Stack spacing={2}>
                <SectionTitle icon={TaskAltIcon} eyebrow="Etapa 4">Confirmação</SectionTitle>
                <TextField select label="Onde registrar" value={targetId} onChange={(event) => setTargetId(event.target.value)}>
                  {eventInfo ? <MenuItem value={presenceTargetValue('event', eventInfo.id)} sx={{ whiteSpace: 'normal' }}>Evento: {eventInfo.nome}</MenuItem> : null}
                  {activities.map((activity) => (
                    <MenuItem key={activity.id} value={presenceTargetValue('activity', activity.id)} sx={{ whiteSpace: 'normal' }}>{activity.nome}</MenuItem>
                  ))}
                </TextField>
                <Box component="dl" sx={{ m: 0 }}>
                  <SummaryRow label="Atividade">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedName || 'Escolha onde registrar'}</Typography>
                  </SummaryRow>
                  <SummaryRow label="Operação">
                    {selectedTarget ? <AvailabilityChips windows={selectedWindows} nowMs={clock} /> : <Typography variant="body2">—</Typography>}
                  </SummaryRow>
                  <SummaryRow label="Horários">
                    <Typography variant="body2">{selectedTarget ? presenceWindowText(selectedWindows) : '—'}</Typography>
                  </SummaryRow>
                  <SummaryRow label="GPS">
                    <Typography variant="body2">
                      {assessment
                        ? `${readingStatusLabel(assessment.status)} · precisão ${assessment.accuracy} m`
                        : locationReading ? `Precisão ${Math.round(locationReading.accuracy)} m` : 'Sem leitura'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">O GPS é lido de novo no momento da confirmação.</Typography>
                  </SummaryRow>
                  <SummaryRow label="Horário">
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{clockFormat.format(clock)} (Brasília)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {clockOffset ? 'Ajustado ao relógio do servidor.' : 'Relógio do aparelho.'} Na confirmação vale o horário do servidor.
                    </Typography>
                  </SummaryRow>
                  <SummaryRow label="Código">
                    <Typography variant="body2" sx={{ color: codeReady ? 'success.main' : 'warning.main', fontWeight: 600 }}>{codeLabel}</Typography>
                  </SummaryRow>
                </Box>
                {inlineFeedback ? feedback : null}
                {error ? (
                  <Typography variant="body2" color="text.secondary">
                    Confira o horário da atividade, sua localização e o código, e tente de novo. Se o problema continuar, procure a organização.
                  </Typography>
                ) : null}
                <Button
                  variant={exitFirst ? 'outlined' : 'contained'}
                  size="large"
                  sx={actionSx}
                  startIcon={<LoginIcon />}
                  disabled={Boolean(busy)}
                  onClick={() => presence('check-in')}
                >
                  {busy === 'check-in' ? 'Confirmando entrada...' : 'Confirmar entrada'}
                </Button>
                <Button
                  variant={exitFirst ? 'contained' : 'outlined'}
                  size="large"
                  sx={actionSx}
                  startIcon={<LogoutIcon />}
                  disabled={Boolean(busy)}
                  onClick={() => presence('check-out')}
                >
                  {busy === 'check-out' ? 'Confirmando saída...' : 'Confirmar saída'}
                </Button>
                <Button variant="text" onClick={() => goToStep(1)}>Voltar</Button>
              </Stack>
            </CardContent></Card>
          </>
        ) : null}

        {inJourney && presenceStep === 3 ? (
          <Card sx={{ borderColor: 'rgba(52, 211, 153, 0.4)' }}><CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} aria-hidden="true" />
                <Box>
                  <Typography variant="overline" component="p" sx={{ color: hackathonColors.sky }}>{'</ resultado >'}</Typography>
                  <Typography variant="h2" component="h2">Presença registrada</Typography>
                </Box>
              </Stack>
              {inlineFeedback ? feedback : null}
              <Typography variant="body2" color="text.secondary">A lista abaixo mostra as presenças confirmadas pelo servidor.</Typography>
              {history.length === 0 ? <Typography color="text.secondary">Nenhuma presença confirmada pelo servidor.</Typography> : null}
              {history.map((item) => (
                <Box key={`${item.event_id}-${item.sub_event_id}-${item.check_in_at}`} sx={panelSx}>
                  <Typography variant="subtitle1">{item.check_out_at ? 'Entrada e saída' : 'Entrada'}</Typography>
                  <Typography>{item.activity_nome || item.event_nome || 'Evento'}</Typography>
                  <Typography variant="body2">Entrada: {formatLocalTimestamp(item.check_in_at)}</Typography>
                  <Typography variant="body2">Saída: {formatLocalTimestamp(item.check_out_at)}</Typography>
                </Box>
              ))}
              <Typography variant="body2" color="text.secondary">
                Próximo passo: ao fim da atividade, volte aqui e confirme a saída na janela de horário.
              </Typography>
              <Button variant="outlined" size="large" sx={actionSx} onClick={() => goToStep(0)}>Registrar outra presença</Button>
            </Stack>
          </CardContent></Card>
        ) : null}
      </Stack>
      <QrScannerDialog
        open={scannerOpen && step === 'journey' && presenceStep === 2}
        onClose={closeScanner}
        onDetected={handleScanned}
      />
    </HackathonShell>
  );
}
